// =============================================================================
// Time windows, expressed as minutes from local midnight.
//
// Everything in this file works in the brand's local wall clock. Converting to
// UTC happens once, at the very end of planning — doing it earlier would make
// "between 18:00 and 21:00" mean different local hours on either side of a DST
// change.
// =============================================================================
export const MINUTES_PER_DAY = 1440;
/** "HH:MM" → minutes from midnight. Throws on anything malformed. */
export function parseClock(value) {
    const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
    if (!m)
        throw new Error(`Expected a HH:MM time, got "${value}"`);
    const hour = Number(m[1]);
    const minute = Number(m[2]);
    if (hour > 23 || minute > 59)
        throw new Error(`"${value}" is not a real time of day`);
    return hour * 60 + minute;
}
/** Minutes from midnight → "HH:MM". Wraps values outside a single day. */
export function formatClock(minutes) {
    const m = ((minutes % MINUTES_PER_DAY) + MINUTES_PER_DAY) % MINUTES_PER_DAY;
    return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}
/**
 * Normalises a window to minute ranges.
 *
 * A window whose end is at or before its start wraps past midnight, so it
 * becomes two ranges. Quiet hours are nearly always of this shape ("22:00" to
 * "06:00"), and treating that as an empty or negative range would silently
 * disable the setting — the user would see posts going out at 3am.
 */
export function toRanges(window) {
    const start = parseClock(window.start);
    const end = parseClock(window.end);
    if (start < end)
        return [{ start, end }];
    if (start === end)
        return [{ start: 0, end: MINUTES_PER_DAY }]; // covers the whole day
    return [
        { start, end: MINUTES_PER_DAY },
        { start: 0, end },
    ];
}
/** Whether a minute-of-day falls inside a window (start inclusive, end exclusive). */
export function windowContains(window, minuteOfDay) {
    return toRanges(window).some((r) => minuteOfDay >= r.start && minuteOfDay < r.end);
}
/** Total minutes a window covers — its weight when choosing between windows. */
export function windowLength(window) {
    return toRanges(window).reduce((sum, r) => sum + (r.end - r.start), 0);
}
/**
 * Validates a posting window.
 *
 * The minimum width exists so the "never :00 or :30" rule below can always be
 * satisfied: inside a 3-minute window there may be no non-round minute to move
 * to, and the scheduler would either loop or quietly give up.
 */
export const MIN_WINDOW_MINUTES = 10;
export function validateWindows(windows) {
    if (windows.length === 0) {
        throw new Error("Add at least one posting window, e.g. 09:00–11:00.");
    }
    for (const w of windows) {
        const length = windowLength(w);
        if (length < MIN_WINDOW_MINUTES) {
            throw new Error(`The window ${w.start}–${w.end} is ${length} minutes long; ` +
                `it needs at least ${MIN_WINDOW_MINUTES} so posts can vary within it.`);
        }
    }
}
/**
 * Picks the minute inside a window that corresponds to `fraction` (0 ≤ f < 1).
 *
 * Expressed as a fraction rather than taking the generator directly so the
 * caller controls how many draws happen — the planner reuses one generator
 * across every decision for a post, and a hidden extra draw here would shift
 * every later choice.
 */
export function minuteAt(window, fraction) {
    const ranges = toRanges(window);
    const total = ranges.reduce((sum, r) => sum + (r.end - r.start), 0);
    let offset = Math.floor(fraction * total);
    for (const range of ranges) {
        const length = range.end - range.start;
        if (offset < length)
            return (range.start + offset) % MINUTES_PER_DAY;
        offset -= length;
    }
    return ranges[ranges.length - 1].end - 1;
}
//# sourceMappingURL=windows.js.map